/* Crucial Tracking — shared brand behavior (Phase 1 foundations).
   Two IntersectionObservers drive the three effects defined in ct-base.css:
   - `.ct-reveal` gets `is-revealed` at ~15% visibility (fires once);
   - `.ct-reveal-group` gets `is-revealed` on itself as soon as it clears
     the bottom 10% of the viewport (threshold 0, so a grid taller than the
     viewport still fires); its `.ct-reveal-item` children stagger in from
     CSS via their inline `--ct-i` index, so a grid costs one observer
     entry, not one per card;
   - `[data-ct-animate]` gets `ct-anim`, triggering trail draws / ping loops.

   Reveal state machine (matches ct-base.css): an element is *pending*
   (hidden) until either the observer sees it -> `is-revealed` (animates
   in), or the CSS failsafe keyframe fires at 6s -> `is-static` (shown, never
   animated). The two paths never cross: the failsafe's animationend marks
   the element/group `is-static` and unobserves it, and init() also treats a
   failsafe that has already finished (script loaded late, missed the event)
   as static via getAnimations(). Nothing that is already visible is ever
   re-hidden.

   Under prefers-reduced-motion the classes are applied immediately — the
   reduced-motion rules in ct-base.css freeze everything in its completed
   state. Idempotent: safe to include twice and re-run on section reloads.

   Also owns the same-page anchor click handler (smooth scroll + pre-reveal
   of the target section); see the comment above it.

   And the behaviour of the redesign header (sections/header.liquid), ported
   from redesign/mockups/shell.js: the mobile menu button (which here also
   closes on an outside click and when the cart notification opens), the
   kit button's target, and the bump of the cart count. The mockup's other
   shared behaviour needs no script here: the accordion is native
   <details>, forms are real Shopify forms, and prices and facts are printed
   by Liquid (snippets/ct-bind).

   And analytics: window.ctTrack(name, data) publishes a Shopify custom
   customer event "ct:<name>", and a click on a link into a kit builder
   publishes "ct:kit_cta_click" through it — on the page the link leads to
   when the click leaves this one; see the comments above each.
   The theme sends nothing to GA4 itself (layout/theme.liquid says why): the
   custom pixel in ops/shopify-customer-events/ct-ga4-kit-events.js forwards
   these events. */

(function () {
  if (window.__ctBaseInit) return;
  window.__ctBaseInit = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var FAILSAFE = 'ct-reveal-failsafe';
  var FAILSAFE_DELAY = 6000; // must match the keyframe delay in ct-base.css

  function isGroup(element) {
    return element.classList.contains('ct-reveal-group');
  }

  function isReveal(element) {
    return element.classList.contains('ct-reveal') || isGroup(element);
  }

  function isSettled(element) {
    return element.classList.contains('is-revealed') || element.classList.contains('is-static');
  }

  function activate(element) {
    if (element.hasAttribute('data-ct-animate')) element.classList.add('ct-anim');
    if (isReveal(element) && !isSettled(element)) element.classList.add('is-revealed');
  }

  function isActivated(element) {
    if (element.hasAttribute('data-ct-animate') && !element.classList.contains('ct-anim')) return false;
    if (isReveal(element) && !isSettled(element)) return false;
    return true;
  }

  // The failsafe keyframe runs on .ct-reveal itself and on a group's items;
  // a finished one means the element is already visible on screen.
  function failsafeFired(element) {
    var probe = isGroup(element) ? element.querySelector('.ct-reveal-item') : element;
    if (!probe) return false;
    if (typeof probe.getAnimations !== 'function') {
      return performance.now() > FAILSAFE_DELAY;
    }
    return probe.getAnimations().some(function (animation) {
      return animation.animationName === FAILSAFE && animation.playState === 'finished';
    });
  }

  function settleStatic(element) {
    if (isSettled(element)) return;
    element.classList.add('is-static');
    if (revealObserver) revealObserver.unobserve(element);
    if (groupObserver) groupObserver.unobserve(element);
  }

  var revealObserver = null;
  var groupObserver = null;

  function onIntersect(observer) {
    return function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        if (isReveal(entry.target) && failsafeFired(entry.target)) {
          settleStatic(entry.target);
          return;
        }
        activate(entry.target);
      });
    };
  }

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      onIntersect(revealObserver)(entries);
    }, { threshold: 0.15 });
    return revealObserver;
  }

  function getGroupObserver() {
    if (groupObserver) return groupObserver;
    groupObserver = new IntersectionObserver(function (entries) {
      onIntersect(groupObserver)(entries);
    }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    return groupObserver;
  }

  function init() {
    var targets = document.querySelectorAll('.ct-reveal, .ct-reveal-group, [data-ct-animate]');

    targets.forEach(function (element) {
      if (isActivated(element)) return;

      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        activate(element);
      } else if (isReveal(element) && failsafeFired(element)) {
        settleStatic(element);
      } else if (isGroup(element)) {
        getGroupObserver().observe(element);
      } else {
        getRevealObserver().observe(element);
      }
    });
  }

  // The failsafe fired before the observer got there: freeze the element (or
  // the item's group) as static so it is never re-hidden and re-animated.
  document.addEventListener('animationend', function (event) {
    if (event.animationName !== FAILSAFE) return;
    var target = event.target;
    if (!target || !target.closest) return;
    var element = target.classList.contains('ct-reveal-item') ? target.closest('.ct-reveal-group') : target;
    if (element && isReveal(element)) settleStatic(element);
  });

  // Same-page anchor CTAs (href="#id" or "/this/path#id"): reveal the target
  // section up front so the customer never lands on content still fading
  // in, then glide to it. The header offset is worked out per click by
  // anchorOffset() below (ct-base.css's root scroll-padding-top covers only
  // the permanently pinned header modes). Anything else — modified clicks,
  // other pages, downloads, a missing or unrendered id — keeps its default
  // navigation.
  var ANCHOR_TARGET = 'ct-anchor-target';
  var ANCHOR_MARK = 'data-ct-anchor-focus'; // the handler added this tabindex; also = blur pending
  var NATIVELY_FOCUSABLE = 'a[href], button, input, select, textarea, summary, [tabindex]';

  // Return a wrapper the handler made focusable to its pre-click state, so
  // a later activation takes the same branch instead of matching
  // [tabindex] and inheriting Dawn's *:focus-visible ring.
  function releaseAnchorTarget(event) {
    var element = event.currentTarget;
    element.classList.remove(ANCHOR_TARGET);
    element.removeAttribute('tabindex');
    element.removeAttribute(ANCHOR_MARK);
  }

  // How much of the viewport top the sticky header (sections/header.liquid)
  // will cover once the glide lands. `always`/`reduce-logo-size` keep it
  // pinned. The default `on-scroll-up` hides it on any downward scroll past
  // its own bounds and reveals it on any upward one — its onScroll listens
  // to the scroll event, so the programmatic glide counts like a swipe — so
  // a target below the viewport top lands flush at the top and one at or
  // above it lands under the revealed header; either choice keeps the
  // scroll direction it assumes. `none` renders no <sticky-header> at all.
  // The redesign header is `always` (sections/header-group.json).
  // This models onScroll's direction rule only, not Dawn's preventReveal
  // (media-gallery.js) / preventHide (details-disclosure.js) overrides,
  // which no current same-page CTA can hit. Measured live because the
  // header's height is responsive and shrinks under `reduce-logo-size`;
  // the announcement bar is a separate section above .section-header and
  // is excluded, matching Dawn's own --header-height.
  // ANCHOR_GAP is the air the redesign mockups leave between the header and
  // an anchor target (their html { scroll-padding-top: header + 16px });
  // ct-base.css sets the same on the root for the pinned modes.
  var ANCHOR_GAP = 16;

  function anchorOffset(targetTop) {
    var sticky = document.querySelector('sticky-header');
    if (!sticky) return 0;
    var type = sticky.getAttribute('data-sticky-type');
    var pinned = type === 'always' || type === 'reduce-logo-size';
    if (!pinned && targetTop > window.scrollY) return 0;
    var header = sticky.closest('.section-header') || sticky;
    return header.getBoundingClientRect().height + ANCHOR_GAP;
  }

  // Same settle rules as init()/onIntersect(): a reveal whose failsafe has
  // already shown it is frozen static, never re-animated.
  function revealNow(scope) {
    var pending = [scope].concat(Array.prototype.slice.call(scope.querySelectorAll('.ct-reveal, .ct-reveal-group')));
    pending.forEach(function (element) {
      if (!isReveal(element) || isSettled(element)) return;
      if (failsafeFired(element)) {
        settleStatic(element);
        return;
      }
      activate(element);
      if (revealObserver) revealObserver.unobserve(element);
      if (groupObserver) groupObserver.unobserve(element);
    });
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest ? event.target.closest('a[href*="#"]') : null;
    if (!link || link.hasAttribute('download')) return;
    if (link.target && link.target !== '_self') return;

    // The anchor's own resolved parts honour <base> and cannot throw.
    if (link.origin !== location.origin || link.pathname !== location.pathname || link.search !== location.search) return;
    if (link.hash.length < 2) return;

    var id = link.hash.slice(1);
    try { id = decodeURIComponent(id); } catch (error) { /* malformed escape: use the raw hash */ }
    var target = document.getElementById(id);
    if (!target) return;

    // An unrendered target needs the browser's own fragment navigation:
    // pushState never updates :target, and main-login shows its recover
    // form purely via #recover:target (customer.css). Closed <details>
    // content likewise stays native so a deep link can auto-open it.
    if (target.getClientRects().length === 0 || target.closest('details:not([open])')) return;

    event.preventDefault();
    revealNow(target);

    // Carry the existing state forward (the pattern in global.js's overlay
    // push): facets.js reads event.state.searchParams on popstate, so a
    // null-state entry would re-render a collection with the wrong filters.
    if (link.hash !== location.hash) {
      var state = Object.assign({ searchParams: location.search.slice(1) }, history.state);
      history.pushState(state, '', link.hash);
    }

    // 'auto' (not 'instant', which older engines reject as a TypeError)
    // resolves to an instant jump because ct-base.css's reduced-motion
    // block sets html { scroll-behavior: auto }.
    var top = target.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, top - anchorOffset(top)), behavior: reducedMotion.matches ? 'auto' : 'smooth' });

    // Natively focusable targets (form-error summary links name <input>
    // ids) just take focus; a section wrapper is made focusable for the
    // moment and marked so ct-base.css draws no whole-section ring.
    // ANCHOR_MARK still present means it is focused from an earlier click
    // with its blur listener pending — add nothing twice.
    if (!target.hasAttribute(ANCHOR_MARK) && !target.matches(NATIVELY_FOCUSABLE)) {
      target.setAttribute('tabindex', '-1');
      target.setAttribute(ANCHOR_MARK, '');
      target.classList.add(ANCHOR_TARGET);
      target.addEventListener('blur', releaseAnchorTarget, { once: true });
    }
    target.focus({ preventScroll: true });
  });

  // -------------------------------------------------------------------------
  // Analytics. The theme configures no GA4 and sends no GA4 event with gtag:
  // GA4 is injected by the Google & YouTube channel (layout/theme.liquid).
  // It publishes Shopify custom customer events instead, and the custom
  // pixel in ops/shopify-customer-events/ct-ga4-kit-events.js forwards them.
  // -------------------------------------------------------------------------

  // window.ctTrack('kit_cta_click', { … }) publishes "ct:kit_cta_click" with
  // page_path added to the data — unless the data names one already: a
  // click held over from the page before (below) reports that page, not
  // this one. Shopify.analytics.publish is Shopify's own; where it is not
  // on the page nothing is sent. Never throws: a click or an add to cart
  // must not fail because an event could not be published.
  // Defined once — a ctTrack already on the window is left as it is.
  if (typeof window.ctTrack !== 'function') {
    window.ctTrack = function (name, data) {
      try {
        var analytics = window.Shopify && window.Shopify.analytics;
        if (!analytics || typeof analytics.publish !== 'function') return;
        var payload = Object.assign({}, data);
        if (payload.page_path == null) payload.page_path = location.pathname;
        analytics.publish('ct:' + name, payload);
      } catch (error) {
        /* an event that can't be published is dropped, never surfaced */
      }
    };
  }

  // A link into a kit builder: a [data-ct-kit-link] (the header's kit button,
  // the sale banner's, the empty cart's), a link to #build-your-kit or to the
  // Trackers band, #trk-builder, on any page (a link set in a template can't
  // carry the attribute), or one to an id here that is or holds the builder.
  // The links inside a builder and the Vehicles / Assets switch above it are
  // the builder's own navigation, not a way in, whatever their hash, so that
  // test comes first. An id that holds the builder only because it holds the
  // whole page (the skip link's #MainContent) is not one either: the target
  // has to sit inside one section.
  function isKitLink(link) {
    if (typeof link.hash !== 'string') return false;
    if (link.closest('[data-kit-builder], [data-trk-switch]')) return false;
    if (link.hasAttribute('data-ct-kit-link') || link.hash === '#build-your-kit' || link.hash === '#trk-builder') return true;
    if (link.hash.length < 2 || link.origin !== location.origin || link.pathname !== location.pathname) return false;

    var id = link.hash.slice(1);
    try { id = decodeURIComponent(id); } catch (error) { /* malformed escape: use the raw hash */ }
    var target = document.getElementById(id);
    if (!target || !target.closest('.shopify-section')) return false;
    return target.matches('[data-kit-builder]') || Boolean(target.querySelector('[data-kit-builder]'));
  }

  // Where the link is: the name its section has in the template or section
  // group, which Shopify prints after the last "__" of the wrapper's id
  // ("shopify-section-template--123__hero" -> "hero",
  // "shopify-section-sections--123__header" -> "header").
  function kitLinkPlacement(link) {
    var section = link.closest('.shopify-section');
    if (!section) return 'unknown';
    var at = section.id.lastIndexOf('__');
    return (at === -1 ? section.id : section.id.slice(at + 2)) || 'unknown';
  }

  // A kit link that takes this tab to another page (the header's button on
  // a page with no builder goes to the vehicle trackers collection) can't be
  // published here: the page is gone before the pixel has loaded gtag.js and
  // sent. So the click is held in sessionStorage, one key, with the time, and
  // the page it leads to publishes it (flushKitClick below).
  var KIT_CLICK_KEY = 'ct_kit_cta';
  var KIT_CLICK_MAX_AGE = 30000; // ms: an older hold is not this navigation's

  // Will the click leave this page? A plain primary click (the keyboard's
  // Enter is one) on a link to another path or query of this site, with no
  // download and no target of its own. A modified click or target="_blank"
  // opens elsewhere and this page stays; a link to another site leads to no
  // page of ours that could publish the hold. Both are published here.
  function kitClickLeaves(event, link) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return false;
    return link.origin === location.origin && (link.pathname !== location.pathname || link.search !== location.search);
  }

  // Returns what was written, or '' when storage refused it (blocked, full):
  // the click is then published at once, which is better than not at all.
  function holdKitClick(data) {
    try {
      var held = JSON.stringify({ at: Date.now(), data: data });
      sessionStorage.setItem(KIT_CLICK_KEY, held);
      return held;
    } catch (error) {
      return '';
    }
  }

  // The click the page before held, published once per page load: from the
  // first init only, so not again when the theme editor reloads a section,
  // and a page restored from the back/forward cache runs no init at all.
  // The key is removed before anything else, so no click is published
  // twice, and a hold past its age is dropped. Never throws.
  var kitClickFlushed = false;
  function flushKitClick() {
    if (kitClickFlushed) return;
    kitClickFlushed = true;
    try {
      var raw = sessionStorage.getItem(KIT_CLICK_KEY);
      if (!raw) return;
      sessionStorage.removeItem(KIT_CLICK_KEY);
      var held = JSON.parse(raw);
      var age = Date.now() - held.at;
      if (!held.data || !(age >= 0 && age < KIT_CLICK_MAX_AGE)) return;
      window.ctTrack('kit_cta_click', { label: held.data.label, placement: held.data.placement, page_path: held.data.page_path });
    } catch (error) {
      /* storage that can't be read, a hold that is not ours: nothing to publish */
    }
  }

  // "ct:kit_cta_click", data { label, placement, page_path }: any click on a
  // kit link, a modified one (Ctrl, Shift) and the keyboard's Enter
  // included. Its own listener, in the capture phase: the anchor handler
  // above returns early for most clicks and cancels the rest, and a
  // listener nearer the link may stop the event before it gets back up to
  // the document.
  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link || !isKitLink(link)) return;
    var label = (link.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60);
    var data = { label: label, placement: kitLinkPlacement(link), page_path: location.pathname };

    var held = kitClickLeaves(event, link) ? holdKitClick(data) : '';
    if (!held) {
      window.ctTrack('kit_cta_click', data);
      return;
    }

    // Held now, before any other listener has seen the click, because the
    // page may be gone before there is a second look. A listener can still
    // cancel it — the type comparison and the fleet picker take their
    // ?tracker= links on the page when a builder here holds that tracker —
    // and then nothing leaves: once the dispatch is over the hold is taken
    // back, if it is still this click's, and the click published here.
    window.setTimeout(function () {
      if (!event.defaultPrevented) return;
      try {
        if (sessionStorage.getItem(KIT_CLICK_KEY) !== held) return;
        sessionStorage.removeItem(KIT_CLICK_KEY);
      } catch (error) {
        return;
      }
      window.ctTrack('kit_cta_click', data);
    }, 0);
  }, true);

  // -------------------------------------------------------------------------
  // Redesign header. Everything is delegated from the document, so a header
  // the theme editor re-renders needs no re-binding.
  // -------------------------------------------------------------------------

  // The mobile menu: one button, one full-width list of the same links.
  // Open and closed are the [hidden] attribute on the list and
  // aria-expanded on the button (ct-shared.css draws the bars as a cross).
  function mobileMenu() {
    var button = document.querySelector('.ct-header .ct-menu-btn');
    var menu = button ? document.getElementById(button.getAttribute('aria-controls')) : null;
    return button && menu ? { button: button, menu: menu } : null;
  }

  function setMobileMenu(open) {
    var parts = mobileMenu();
    if (!parts) return;
    parts.menu.hidden = !open;
    parts.button.setAttribute('aria-expanded', open ? 'true' : 'false');
    var label = parts.button.getAttribute(open ? 'data-label-close' : 'data-label-open');
    if (label) parts.button.setAttribute('aria-label', label);
  }

  // The button toggles. While the menu is open it also closes on a link
  // inside it (the page is about to change or scroll) and on a click or tap
  // anywhere outside it; a click on the menu's own padding leaves it open.
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var parts = mobileMenu();
    if (!parts) return;
    if (event.target.closest('.ct-menu-btn') === parts.button) {
      setMobileMenu(parts.menu.hidden);
    } else if (!parts.menu.hidden) {
      if (!parts.menu.contains(event.target) || event.target.closest('a')) setMobileMenu(false);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var parts = mobileMenu();
    if (!parts || parts.menu.hidden) return;
    setMobileMenu(false);
    parts.button.focus();
  });

  // The menu button is hidden from 990px (ct-shared.css); a menu left open
  // while the window grows past that is closed, as in the mockup.
  var desktopHeader = window.matchMedia('(min-width: 990px)');
  function closeMenuOnDesktop() {
    if (desktopHeader.matches) setMobileMenu(false);
  }
  if (typeof desktopHeader.addEventListener === 'function') {
    desktopHeader.addEventListener('change', closeMenuOnDesktop);
  } else if (typeof desktopHeader.addListener === 'function') {
    desktopHeader.addListener(closeMenuOnDesktop);
  }

  // The header's kit button ([data-ct-kit-link], "Shop trackers") and the sale
  // banner's link are printed pointing at the vehicle trackers collection, at
  // the top of its band (#trk-builder; Kris, 2026-10-30). On a page with a
  // kit builder of its own they open that one instead; the anchor handler
  // above then glides to it. The header's button on the home page also
  // carries data-ct-kit-fixed (sections/header.liquid), as the empty cart's
  // button always does (sections/main-cart-items.liquid), and those two are
  // left as printed: they keep data-ct-kit-link, so isKitLink() still counts
  // them and the click is held across the navigation (kitClickLeaves).
  function pointKitLinks() {
    var builder = document.getElementById('build-your-kit') || document.querySelector('[data-kit-builder]');
    if (!builder) return;
    if (!builder.id) builder.id = 'build-your-kit';
    Array.prototype.forEach.call(document.querySelectorAll('[data-ct-kit-link]:not([data-ct-kit-fixed])'), function (link) {
      link.setAttribute('href', '#' + builder.id);
    });
  }

  // The cart count is re-rendered by Dawn (cart.js, cart-notification.js)
  // and by ct-product.js: they replace the inner HTML of #cart-icon-bubble.
  // Mark the fresh count so ct-chrome.css bumps it once.
  var observedBubble = null;
  function watchCartCount() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || bubble === observedBubble || !('MutationObserver' in window)) return;
    observedBubble = bubble;
    new MutationObserver(function () {
      var count = bubble.querySelector('.cart-count-bubble');
      if (count) count.classList.add('is-bumped');
    }).observe(bubble, { childList: true });
  }

  // The cart notification (snippets/cart-notification, Dawn) drops out from
  // under the header into the space an open menu hangs over, and the menu is
  // painted above it. Dawn opens it by adding .active; when that happens the
  // menu gets out of the way. (An add-to-cart button is outside the menu, so
  // the click handler above has usually closed it already; this also covers
  // an add that no click on the page started.)
  var observedNotification = null;
  function watchCartNotification() {
    var notification = document.getElementById('cart-notification');
    if (!notification || notification === observedNotification || !('MutationObserver' in window)) return;
    observedNotification = notification;
    new MutationObserver(function () {
      if (notification.classList.contains('active')) setMobileMenu(false);
    }).observe(notification, { attributes: true, attributeFilter: ['class'] });
  }

  function initChrome() {
    pointKitLinks();
    watchCartCount();
    watchCartNotification();
  }

  function initAll() {
    init();
    initChrome();
    flushKitClick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-scan when the theme editor injects or re-renders a section.
  document.addEventListener('shopify:section:load', initAll);
})();
